import { Col, Form, Input, Row, Select } from "antd"
import { useEffect } from "react"

import CreatableOptionSelect from "#blocks/CreatableOptionSelect"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import Firebase from "#classes/Firebase"
import SkillLevelDropdown from "#components/forms/SkillLevelDropdown"
import { CLASS_GROUPS } from "#constants/classOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  AddStudentFormValues,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { normalizeEmailFormValue } from "#utils/emailHelpers"
import { filterFormOptions } from "#utils/formHelpers"

const { Option } = Select

const AddNewStudentForm = ({
  initialValues,
}: {
  initialValues: StudentConstructor | undefined | null
}) => {
  const form = Form.useFormInstance<AddStudentFormValues>()

  const canSeeContactInfo = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )

  // Generate a student ID upfront if this is a new student
  useEffect(() => {
    const currentStudentId = form.getFieldValue("id")
    if (!currentStudentId && !initialValues?.id) {
      const newRef = Firebase.getInstance().getNewRef(DOC_TYPES.STUDENTS)
      form.setFieldValue("id", newRef.id)
    }
  }, [form, initialValues?.id])

  return (
    <>
      <Row gutter={40}>
        <Col span={14}>
          <SFormHeader>Student Info</SFormHeader>

          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="name"
                label="Display name (used as the main name)"
              >
                <Input placeholder="Leave blank to auto-generate" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={7}>
              <Form.Item
                name="firstName"
                label="First name"
                rules={[
                  {
                    required: true,
                    message: "Please input the student name!",
                  },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="lastName"
                label="Last name"
                rules={[
                  {
                    required: true,
                    message: "Please input the student name!",
                  },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                name="dob"
                label="Date of birth"
                rules={[
                  { required: true, message: "Please select a date of birth!" },
                ]}
              >
                <Input placeholder="Date of birth" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="gender" label="Gender">
                <Select
                  placeholder="Gender"
                  filterOption={filterFormOptions}
                  showSearch
                >
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {canSeeContactInfo && (
            <Row gutter={16}>
              <Col span={7}>
                <Form.Item
                  name="personalPhone"
                  label={
                    <Flex center gap={6}>
                      Personal phone
                    </Flex>
                  }
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  name="personalEmail"
                  label={
                    <Flex center gap={6}>
                      Personal email
                    </Flex>
                  }
                  normalize={normalizeEmailFormValue}
                >
                  <Input
                    placeholder="Enter personal email"
                    type="email"
                    autoComplete="email"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={7}>
              <SkillLevelDropdown
                classGroup={CLASS_GROUPS.CORE}
                fieldName="coreSkillLevel"
              />
            </Col>
            <Col span={7}>
              <SkillLevelDropdown
                classGroup={CLASS_GROUPS.DIGITAL_ART}
                fieldName="digitalSkillLevel"
              />
            </Col>
            <Col span={7}>
              <SkillLevelDropdown
                classGroup={CLASS_GROUPS.SCULPTURE}
                fieldName="sculptureSkillLevel"
              />
            </Col>
          </Row>

          {canSeeContactInfo && (
            <>
              <SFormHeader>
                <Flex style={{ alignItems: "center" }} gap={12}>
                  Primary Address
                </Flex>
              </SFormHeader>
              <Row gutter={16}>
                <Col span={10}>
                  <Form.Item
                    name={["primaryAddress", "street1"]}
                    label="Street"
                  >
                    <Input placeholder="Enter street" />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name={["primaryAddress", "street2"]}
                    label="Street 2"
                  >
                    <Input placeholder="Enter street 2" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={7}>
                  <Form.Item name={["primaryAddress", "city"]} label="City">
                    <Input placeholder="Enter city" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={["primaryAddress", "state"]} label="State">
                    <Input placeholder="Enter state" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    name={["primaryAddress", "zipCode"]}
                    label="Zip code"
                  >
                    <Input placeholder="Enter zip code" />
                  </Form.Item>
                </Col>
              </Row>

              <SFormHeader>Guardian Info</SFormHeader>
              <Row gutter={16}>
                <Col span={10}>
                  <Form.Item name="guardian1Name" label="Guardian 1 name">
                    <Input placeholder="Enter guardian 1 name" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="guardian1Phone"
                    label={
                      <Flex center gap={6}>
                        Phone number
                      </Flex>
                    }
                  >
                    <Input placeholder="Guardian 1 phone" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="guardian1Email"
                    label={
                      <Flex center gap={6}>
                        Email
                      </Flex>
                    }
                    normalize={normalizeEmailFormValue}
                  >
                    <Input
                      placeholder="Guardian 1 email"
                      type="email"
                      autoComplete="email"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={10}>
                  <Form.Item name="guardian2Name" label="Guardian 2 name">
                    <Input placeholder="Enter guardian 2 name" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="guardian2Phone"
                    label={
                      <Flex center gap={6}>
                        Phone number
                      </Flex>
                    }
                  >
                    <Input placeholder="Guardian 2 phone" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="guardian2Email"
                    label={
                      <Flex center gap={6}>
                        Email
                      </Flex>
                    }
                    normalize={normalizeEmailFormValue}
                  >
                    <Input
                      placeholder="Guardian 2 email"
                      type="email"
                      autoComplete="email"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Col>

        <Col span={10}>
          <Row gutter={16}>
            <SFormHeader>Allergies</SFormHeader>
            <Col span={23}>
              <Form.Item name="allergies">
                <CreatableOptionSelect
                  docType={DOC_TYPES.ALLERGIES}
                  placeholder="Select or add allergies"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={23}>
              <SFormHeader>Tags</SFormHeader>
              <Form.Item name="tags">
                <CreatableOptionSelect
                  docType={DOC_TYPES.TAGS}
                  placeholder="Select or add tags"
                />
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Notes</SFormHeader>
          <Row gutter={16}>
            <Col span={23}>
              <Form.Item name="notes">
                <Input.TextArea placeholder="Enter notes here" rows={10} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="avatar" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </>
  )
}

export default AddNewStudentForm
